import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CalendarClock, CheckCircle2, AlertCircle, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = "Africa/Lagos";
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: TZ });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
const lagosDate = (d) => d.toLocaleDateString("en-CA", { timeZone: TZ });

const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAFAF9] py-8 px-4" data-testid={testid}>
    <div className="max-w-xl mx-auto space-y-5">
      <img src="/platemind-logo-new.png" alt="PlateMind" className="h-9 w-auto" />
      {children}
    </div>
  </div>
);

const buildDates = (win) => {
  const out = [];
  if (!win) return out;
  const d = new Date(`${lagosDate(new Date(win.earliest))}T12:00:00`);
  const last = new Date(`${win.latest_date}T12:00:00`);
  while (d <= last) { if (d.getDay() % 6 !== 0) out.push(new Date(d)); d.setDate(d.getDate() + 1); }
  return out;
};

export default function ManageAppointment() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [confirming, setConfirming] = useState(false);
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState(null);
  const [win, setWin] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotMsg, setSlotMsg] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/booking/manage/${token}`).then(async r => {
      const d = await r.json();
      if (!r.ok) { setError(d.detail); return; }
      setAppt(d);
      if (params.get("confirm") === "1" && d.can_confirm && !d.attendance_confirmed_at) confirmAttendance(d);
    }).catch(() => setError("Connection problem."));
    fetch(`${API}/booking/window`).then(r => r.json()).then(setWin).catch(() => {});
  }, [token]);

  const confirmAttendance = async (current) => {
    setConfirming(true);
    try {
      const r = await fetch(`${API}/booking/manage/${token}/confirm-attendance`, { method: "POST" });
      const d = await r.json();
      if (r.ok) setAppt({ ...current, attendance_confirmed_at: d.attendance_confirmed_at });
      else toast.error(d.detail || "Could not confirm your attendance.");
    } catch { toast.error("Connection problem."); } finally { setConfirming(false); }
  };

  const loadSlots = async (d) => {
    setDate(d); setPicked(null); setLoadingSlots(true);
    try {
      const r = await fetch(`${API}/booking/manage/${token}/slots?date=${lagosDate(d)}`);
      const data = await r.json();
      setSlots(data.slots || []); setSlotMsg(data.message || "");
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/booking/manage/${token}/reschedule`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ appointment_datetime: picked.datetime_utc }) });
      const d = await r.json();
      if (r.ok) setDone(d);
      else { toast.error(d.detail || "Could not move your appointment."); if (r.status === 409) loadSlots(date); }
    } catch { toast.error("Connection problem."); } finally { setSaving(false); }
  };

  if (error) return <Shell testid="manage-error"><Card className="rounded-2xl p-8 text-center"><AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-sm text-stone-600">{error}</p></Card></Shell>;
  if (!appt) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" /></div>;
  if (done) return (
    <Shell testid="reschedule-success">
      <Card className="rounded-2xl p-8 text-center space-y-3">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
        <h1 className="text-xl font-bold text-stone-900">Appointment moved</h1>
        <p className="text-sm text-stone-600" data-testid="new-appointment-time">{fmtDate(done.appointment_datetime)} at {fmtTime(done.appointment_datetime)} WAT</p>
        <p className="text-xs text-stone-400">We've emailed you the updated details{done.meeting_url ? " and your new meeting link" : ""}.</p>
      </Card>
    </Shell>
  );

  const dates = buildDates(win);
  return (
    <Shell testid="manage-appointment-page">
      <div>
        <p className="text-xs font-bold text-[#FF7A00] uppercase tracking-widest">Your appointment</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-1">Hello {appt.first_name}, {appt.attendance_confirmed_at ? "see you soon" : "will you be there?"}</h1>
      </div>
      <Card className="rounded-2xl p-5 border-stone-200" data-testid="current-appointment">
        <p className="text-xs text-stone-400 mb-1">Currently booked</p>
        <p className="text-base font-bold text-stone-900">{fmtDate(appt.appointment_datetime)}, {fmtTime(appt.appointment_datetime)} WAT</p>
        <p className="text-xs text-stone-500 mt-1 flex items-center gap-1.5"><Video className="w-3.5 h-3.5" />{appt.consultation_type === "initial" ? "Initial consultation" : "Follow-up session"}, {appt.duration_minutes} minutes</p>
      </Card>
      {appt.attendance_confirmed_at ? (
        <Card className="rounded-2xl p-5 bg-green-50 border-green-200 flex items-center gap-3" data-testid="attendance-confirmed">
          <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
          <p className="text-sm text-green-900">Thank you, you're confirmed. Your dietitian knows you'll attend.</p>
        </Card>
      ) : appt.can_confirm && (
        <Button onClick={() => confirmAttendance(appt)} disabled={confirming} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 font-bold" data-testid="confirm-attendance-btn">
          {confirming ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}Confirm I'll attend
        </Button>
      )}
      {appt.can_reschedule && <p className="text-sm font-semibold text-stone-800 pt-2">Can't make it? Choose a new time below.</p>}
      {!appt.can_reschedule ? (
        <Card className="rounded-2xl p-5 bg-amber-50 border-amber-200 space-y-3" data-testid="reschedule-blocked">
          <p className="text-sm text-amber-900">{appt.reason}</p>
          <a href="https://wa.me/2347062039281?text=Hello%20PlateMind%2C%20I%20need%20to%20change%20my%20appointment." target="_blank" rel="noreferrer" className="inline-block bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full">Message us on WhatsApp</a>
        </Card>
      ) : (
        <>
          <div>
            <p className="text-sm font-semibold text-stone-800 mb-2">Choose a new day</p>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="reschedule-dates">
              {dates.map(d => (
                <button key={d.toISOString()} onClick={() => loadSlots(d)} data-testid={`resched-date-${lagosDate(d)}`}
                  className={`shrink-0 w-16 py-2.5 rounded-xl text-center transition-colors ${date && lagosDate(date) === lagosDate(d) ? "bg-[#FF7A00] text-white" : "bg-white border border-stone-200 hover:border-[#FF7A00]/50"}`}>
                  <span className="block text-[10px] uppercase">{d.toLocaleDateString("en-GB", { weekday: "short" })}</span>
                  <span className="block text-lg font-bold">{d.getDate()}</span>
                  <span className="block text-[10px]">{d.toLocaleDateString("en-GB", { month: "short" })}</span>
                </button>
              ))}
            </div>
          </div>
          {date && (
            <div>
              <p className="text-xs text-stone-400 mb-2">All times in {win?.timezone_label || "West Africa Time (WAT)"}</p>
              {loadingSlots ? <Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]" /> : slots.length ? (
                <div className="grid grid-cols-4 gap-2" data-testid="reschedule-slots">
                  {slots.map(s => (
                    <button key={s.datetime_utc} onClick={() => setPicked(s)} data-testid={`resched-slot-${s.time}`}
                      className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${picked?.datetime_utc === s.datetime_utc ? "bg-[#FF7A00] text-white" : "bg-white border border-stone-200 hover:border-[#FF7A00]/50"}`}>{s.time}</button>
                  ))}
                </div>
              ) : <p className="text-sm text-stone-500">{slotMsg || "No times available on this day."}</p>}
            </div>
          )}
          {picked && (
            <Button onClick={confirm} disabled={saving} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 font-bold" data-testid="confirm-reschedule-btn">
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CalendarClock className="w-5 h-5 mr-2" />}Move to {fmtDate(picked.datetime_utc)}, {picked.time}
            </Button>
          )}
          <p className="text-[11px] text-stone-400 text-center">You can move your appointment {appt.reschedules_left} more time{appt.reschedules_left === 1 ? "" : "s"} online. To cancel, message us on WhatsApp.</p>
        </>
      )}
    </Shell>
  );
}
